/**
 * Personal AI Trainer — main camera + form-bar + rep-counter component.
 *
 * Two render modes:
 *   • mode='full'  → fills 60% of screen with rep counter overlay
 *   • mode='pip'   → 110×148 corner window, draggable to 4 corners
 *
 * Single tap toggles between modes. Form bar renders at the *visible*
 * camera edge (top in full mode, bottom in PiP).
 *
 * Parent supplies:
 *   exerciseName    — current exercise label
 *   onSetEnd        — called with { reps, partialReps, holdSeconds, formBarPct, issuesDetected[] }
 *   active          — boolean (engine runs only when true)
 *   onTelemetry     — optional, called with full snapshot at frequent intervals
 *   fullScreen      — optional; renders a true 100vh overlay (no LiveSession
 *                     chrome assumed) with a close button, instead of the
 *                     default 60vh panel. Tap-to-PiP is disabled in this mode.
 *   onClose         — required when fullScreen is set; called on close tap
 */
import { useEffect, useImperativeHandle, useRef, useState, forwardRef, useCallback } from "react";
import { ChevronDown, X } from "lucide-react";
import usePersonalAITrainerEngine from "./usePersonalAITrainerEngine";
import SuggestionSheet from "./SuggestionSheet";
import ManualCounter from "./ManualCounter";

const FORM_BAR_COLOURS = {
  green: "rgba(0,160,80,0.85)",
  amber: "rgba(200,160,0,0.85)",
  red: "rgba(192,57,43,0.85)",
};

const PIP_CORNERS = {
  "bottom-right": { bottom: 90, right: 12 },
  "bottom-left": { bottom: 90, left: 12 },
  "top-right": { top: 76, right: 12 },
  "top-left": { top: 76, left: 12 },
};

const PersonalAITrainer = forwardRef(function PersonalAITrainer(
  { exerciseName, onSetEnd, active = true, manualOverride = false, onTelemetry, fullScreen = false, onClose },
  parentRef
) {
  const engine = usePersonalAITrainerEngine({ exerciseName, aiOn: active && !manualOverride });
  const [mode, setMode] = useState("full"); // 'full' | 'pip'
  const [pipCorner, setPipCorner] = useState("bottom-right");
  const [showSuggestion, setShowSuggestion] = useState(null);
  const [repFlash, setRepFlash] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const lastSeenIssueRef = useRef({}); // tracks per-set issue presence
  const issuesShownThisWorkoutRef = useRef(new Set());
  const manualRepsRef = useRef(0);

  // ─── Boot engine ─────────────────────────────────────────────────
  useEffect(() => {
    if (manualOverride) return;
    if (!active) return;
    if (!videoRef.current) return;
    engine.start(videoRef.current, canvasRef.current);
    return () => engine.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, manualOverride]);

  // ─── Track issues per set ─────────────────────────────────────────
  useEffect(() => {
    if (!engine.issueKey) return;
    lastSeenIssueRef.current[engine.issueKey] =
      (lastSeenIssueRef.current[engine.issueKey] || 0) + 1;
  }, [engine.issueKey]);

  // ─── Rep flash animation ─────────────────────────────────────────
  useEffect(() => {
    if (engine.reps === 0) return;
    setRepFlash(true);
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(15);
    const t = setTimeout(() => setRepFlash(false), 200);
    return () => clearTimeout(t);
  }, [engine.reps]);

  // ─── Imperative API for the parent (set-end handler) ─────────────
  useImperativeHandle(parentRef, () => ({
    /**
     * Called by the parent screen at the moment "complete set" fires.
     * Returns set telemetry, triggers suggestion if warranted, and resets.
     */
    finaliseSet: () => {
      const { green, amber, red } = engine.formBarPct;
      const issuesDetected = Object.keys(lastSeenIssueRef.current);
      const reps = manualOverride ? manualRepsRef.current : engine.reps;
      const holdSeconds = engine.holdSeconds;
      const partialReps = engine.partialReps;
      const issueDuration = amber + red;

      // Only show suggestion once per workout per issue, and only on supported kinds
      let suggestion = null;
      if (
        !manualOverride &&
        issueDuration > 30 &&
        ["squat", "push_up", "plank"].includes(engine.kind) &&
        issuesDetected.length > 0
      ) {
        const candidate = issuesDetected[0];
        if (!issuesShownThisWorkoutRef.current.has(candidate)) {
          suggestion = candidate;
          issuesShownThisWorkoutRef.current.add(candidate);
        }
      }
      if (suggestion) setShowSuggestion(suggestion);

      const payload = {
        reps,
        partialReps,
        holdSeconds,
        formBarPct: { green, amber, red },
        issuesDetected,
        suggestionShown: suggestion,
        kind: engine.kind,
        manual: manualOverride,
      };
      onSetEnd?.(payload);
      // Reset for next set
      lastSeenIssueRef.current = {};
      manualRepsRef.current = 0;
      engine.resetSet();
      return payload;
    },
    fpsMode: engine.fpsMode,
    status: engine.status,
    detected: engine.detected,
  }));

  // ─── Periodic telemetry ──────────────────────────────────────────
  useEffect(() => {
    if (!onTelemetry) return;
    const id = setInterval(() => {
      onTelemetry({
        reps: engine.reps,
        formBar: engine.formBar,
        detected: engine.detected,
        status: engine.status,
        fpsMode: engine.fpsMode,
      });
    }, 1000);
    return () => clearInterval(id);
  }, [engine.reps, engine.formBar, engine.detected, engine.status, engine.fpsMode, onTelemetry]);

  const isManualPath =
    manualOverride ||
    engine.status === "permission_denied" ||
    engine.status === "no_camera" ||
    engine.status === "unsupported" ||
    engine.status === "error";

  const renderRepDisplay = () => {
    let main = "—";
    let sub = "reps";
    if (!engine.detected && engine.kind !== "plank") {
      main = "—";
    } else if (engine.kind === "plank") {
      const m = Math.floor(engine.holdSeconds / 60);
      const s = String(engine.holdSeconds % 60).padStart(2, "0");
      main = `${m}:${s}`;
      sub = "hold";
    } else {
      main = String(engine.reps);
    }
    return { main, sub };
  };

  const display = renderRepDisplay();
  const formColour = FORM_BAR_COLOURS[engine.formBar] || FORM_BAR_COLOURS.green;

  // ─── Status overlays (loading / permission / no-camera) ──────────
  const renderStatusOverlay = () => {
    if (engine.status === "loading") {
      return (
        <div className="absolute inset-0 grid place-items-center" style={{ background: "rgba(15,15,15,0.96)" }}>
          <div className="text-center px-6">
            <div className="w-40 h-1 bg-white/10 rounded-full overflow-hidden mx-auto">
              <div
                className="h-full"
                style={{
                  background: "#00D4FF",
                  width: "40%",
                  animation: "pat-loading 1.4s ease-in-out infinite",
                }}
              />
            </div>
            <div
              className="mt-3"
              style={{
                fontFamily: "Barlow, sans-serif",
                fontSize: 13,
                fontWeight: 500,
                color: "rgba(255,255,255,0.55)",
              }}
            >
              Starting Personal AI Trainer…
            </div>
          </div>
          <style>{`@keyframes pat-loading {
            0% { transform: translateX(-100%); width: 30%; }
            50% { transform: translateX(120%); width: 50%; }
            100% { transform: translateX(-100%); width: 30%; }
          }`}</style>
        </div>
      );
    }
    if (engine.status === "permission_denied") {
      return (
        <div
          className="absolute inset-0 grid place-items-center px-6"
          style={{ background: "rgba(15,15,15,0.96)", color: "white" }}
          data-testid="pat-permission-denied"
        >
          <div className="text-center">
            <h3 style={{ fontFamily: "Barlow Condensed", fontSize: 20, fontWeight: 700 }}>
              Camera access needed
            </h3>
            <p className="mt-2 text-[13px]" style={{ color: "rgba(255,255,255,0.65)" }}>
              Go to your phone settings and allow camera access for FitFrik.
            </p>
            <button
              type="button"
              onClick={() => engine.retry()}
              className="mt-3 px-4 h-10 rounded-pill text-[13px] font-bold"
              style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}
              data-testid="pat-permission-retry"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    if (engine.status === "no_camera" || engine.status === "unsupported") {
      return (
        <div
          className="absolute inset-0"
          style={{ background: "rgba(15,15,15,0.96)" }}
          data-testid="pat-no-camera"
        >
          <ManualCounter
            exerciseName={exerciseName}
            onRepsChange={(r) => (manualRepsRef.current = r)}
          />
        </div>
      );
    }
    if (engine.status === "error") {
      return (
        <div
          className="absolute inset-0 grid place-items-center px-6"
          style={{ background: "rgba(15,15,15,0.96)", color: "white" }}
          data-testid="pat-error"
        >
          <div className="text-center">
            <h3 style={{ fontFamily: "Barlow Condensed", fontSize: 20, fontWeight: 700 }}>
              Detection stopped working
            </h3>
            <p className="mt-2 text-[13px]" style={{ color: "rgba(255,255,255,0.65)" }}>
              The pose model kept failing to read the camera. Try again, or check your browser console for details.
            </p>
            <button
              type="button"
              onClick={() => engine.retry()}
              className="mt-3 px-4 h-10 rounded-pill text-[13px] font-bold"
              style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}
              data-testid="pat-error-retry"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  // ─── PiP corner drag (snap to nearest corner) ────────────────────
  const handlePipDragEnd = useCallback((e) => {
    if (mode !== "pip") return;
    const x = e.changedTouches?.[0]?.clientX ?? e.clientX;
    const y = e.changedTouches?.[0]?.clientY ?? e.clientY;
    if (x == null || y == null) return;
    const isLeft = x < window.innerWidth / 2;
    const isTop = y < window.innerHeight / 2;
    setPipCorner(`${isTop ? "top" : "bottom"}-${isLeft ? "left" : "right"}`);
  }, [mode]);

  // Manual fallback path — shows tap counter, no camera
  if (isManualPath && !engine.status === "loading") {
    return (
      <div
        className={fullScreen ? "fixed inset-0 z-30 px-3 pt-16" : "fixed inset-x-0 top-[68px] bottom-[110px] z-30 px-3"}
        data-testid="pat-manual-mode"
      >
        {fullScreen && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close AI Trainer"
            data-testid="pat-close"
            className="absolute top-3 right-3 w-9 h-9 grid place-items-center rounded-full bg-black/60 text-white"
          >
            <X size={18} />
          </button>
        )}
        <ManualCounter
          exerciseName={exerciseName}
          onRepsChange={(r) => (manualRepsRef.current = r)}
        />
      </div>
    );
  }

  return (
    <>
      {/* Main camera surface */}
      <div
        className="fixed z-30 overflow-hidden bg-black"
        data-testid="pat-camera-surface"
        data-mode={fullScreen ? "fullscreen" : mode}
        onClick={fullScreen ? undefined : () => setMode((m) => (m === "full" ? "pip" : "full"))}
        onTouchEnd={fullScreen ? undefined : handlePipDragEnd}
        style={
          fullScreen
            ? { top: 0, left: 0, right: 0, bottom: 0, height: "100vh", borderRadius: 0 }
            : mode === "full"
            ? {
                top: 64,
                left: 0,
                right: 0,
                height: "60vh",
                borderRadius: 0,
                transition: "all 200ms ease",
              }
            : {
                width: 110,
                height: 148,
                borderRadius: 14,
                border: "1px solid rgba(255,255,255,0.3)",
                boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                transition: "all 200ms ease",
                ...PIP_CORNERS[pipCorner],
              }
        }
      >
        <video
          ref={videoRef}
          playsInline
          muted
          disablePictureInPicture
          disableRemotePlayback
          className="w-full h-full object-cover"
          style={{ transform: "scaleX(-1)" /* mirror */ }}
        />
        {/* Full HUD overlay — skeleton, info panel, stats badge, center
            banners — drawn imperatively in usePersonalAITrainerEngine's RAF
            loop (drawOverlay). NOT CSS-mirrored: it manually mirrors each
            landmark's x-coordinate to match the mirrored video beneath it,
            so the text/panels it draws stay upright and legible. */}
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        />

        {/* Form bar — 6px at the visible edge */}
        <div
          data-testid="pat-form-bar"
          data-state={engine.formBar}
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            height: 6,
            background: formColour,
            transition: "background 180ms ease",
            ...(mode === "full" ? { top: 0 } : { bottom: 0 }),
          }}
        />

        {fullScreen && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClose?.(); }}
            aria-label="Close AI Trainer"
            data-testid="pat-close"
            className="absolute top-3 left-3 w-9 h-9 grid place-items-center rounded-full bg-black/50 text-white"
            style={{ zIndex: 1 }}
          >
            <X size={18} />
          </button>
        )}

        {/* Status dot */}
        <div
          data-testid="pat-status-dot"
          data-detected={engine.detected ? "true" : "false"}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: engine.detected ? "#00D14F" : "rgba(255,255,255,0.5)",
            animation: engine.detected ? "none" : "pat-pulse 1.2s ease-in-out infinite",
          }}
        />

        {/* Reduced-accuracy indicator */}
        {engine.fpsMode !== "standard" && (
          <div
            style={{
              position: "absolute",
              bottom: 8,
              left: 8,
              fontFamily: "Barlow, sans-serif",
              fontSize: 9,
              fontWeight: 500,
              color: "rgba(255,255,255,0.5)",
            }}
            data-testid="pat-reduced-mode"
          >
            Reduced accuracy mode
          </div>
        )}

        {/* Rep counter — full mode only */}
        {/* Rep count, form score, posture, angles, and feedback are all now
            drawn directly on the canvas (drawOverlay in the engine hook) —
            a single professional HUD matching the reference AI-Personal-Coach
            app, instead of duplicating the same numbers in separate React-
            rendered panels. `repFlash` below still drives the haptic buzz. */}

        {/* PiP rep counter (small) — canvas HUD panels are skipped below
            260px, so this compact number is the only readout in PiP mode. */}
        {mode === "pip" && (
          <div
            className="absolute inset-0 grid place-items-center pointer-events-none"
            data-testid="pat-rep-counter-pip"
          >
            <div
              style={{
                fontFamily: '"Barlow Condensed", sans-serif',
                fontSize: 36,
                fontWeight: 900,
                color: "white",
                textShadow: "0 1px 4px rgba(0,0,0,0.5)",
              }}
            >
              {display.main}
            </div>
          </div>
        )}

        {/* Mode hint chip */}
        {mode === "pip" && (
          <div
            style={{
              position: "absolute",
              top: 6,
              left: 6,
              padding: "2px 6px",
              fontSize: 9,
              fontWeight: 700,
              color: "white",
              background: "rgba(0,0,0,0.55)",
              borderRadius: 4,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            <ChevronDown size={9} style={{ display: "inline", marginRight: 2 }} /> Tap to expand
          </div>
        )}

        {renderStatusOverlay()}
        <style>{`@keyframes pat-pulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.4); }
        }`}</style>
      </div>

      {showSuggestion && (
        <SuggestionSheet issueKey={showSuggestion} onDismiss={() => setShowSuggestion(null)} />
      )}
    </>
  );
});

export default PersonalAITrainer;
